import {useEffect, useState, type FormEvent} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useMutation} from '@tanstack/react-query'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {HelpIcon} from '@/components/help-icon'
import {Tag} from '@/components/ui/tag'
import {Skeleton} from '@/components/ui/skeleton'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {FieldError} from '@/components/field-error'
import {LicenciaSstDetail} from '@/components/licencia-sst-section'
import {useAuth} from '@/hooks/platform/use-auth'
import {useMyBusinessesPaged} from '@/hooks/empresa/organizacion/use-businesses'
import {useMyLicencia} from '@/hooks/inci/consola/use-licencia'
import {api} from '@/lib/api'
import {translateApiError} from '@/lib/errors'
import {validatePassword} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

const EMPTY = {current: '', next: '', confirm: ''}

/** Licencia SST (Res. 908/2025) belongs to the user, not the membership: «Mi cuenta» is the
 * only surface that also reaches an owner. */
function LicenciaSstAccountSection({contenedor}: {contenedor: HTMLElement | null}) {
  const {t} = useTranslation()
  const {data: licencia, isLoading} = useMyLicencia()

  if (isLoading) return <Skeleton className="mt-3 h-32 w-full" />
  if (!licencia) return null

  return (
    <div className="space-y-2 border-t border-border pt-4">
      <p className="flex items-center gap-1.5 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
        {t('licencia.sectionTitle')}
        {/* Dentro de un diálogo, el popover se porta al DialogContent o Escape
            cierra el diálogo entero en vez del popover (ver popover.tsx). */}
        <HelpIcon label={t('licencia.sectionTitle')} testId="licencia-account-tip" contenedor={contenedor}>
          {t('licencia.accountHint')}
        </HelpIcon>
      </p>
      <LicenciaSstDetail licencia={licencia} />
    </div>
  )
}

export function MiCuentaDialog({open, onOpenChange}: {open: boolean; onOpenChange: (open: boolean) => void}) {
  const {t} = useTranslation()
  const {user} = useAuth()
  const [form, setForm] = useState(EMPTY)
  const [error, setError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [bizQuery, setBizQuery] = useState('')
  const [bizSearch, setBizSearch] = useState('')
  const [bizPage, setBizPage] = useState(1)
  // Ref de CALLBACK, no `useRef` + `useEffect`: Radix monta el contenido del
  // diálogo en un commit posterior al primer efecto (ver popover.tsx).
  const [dialogNode, setDialogNode] = useState<HTMLElement | null>(null)

  // Debounce the search so an admin's big directory isn't queried per keystroke.
  useEffect(() => {
    const id = setTimeout(() => {
      setBizSearch(bizQuery.trim())
      setBizPage(1)
    }, 250)
    return () => clearTimeout(id)
  }, [bizQuery])

  const {data: bizPageData} = useMyBusinessesPaged(bizPage, bizSearch, open)

  function reset() {
    setForm(EMPTY)
    setError(null)
    setPasswordError(null)
    setDone(false)
    setBizQuery('')
    setBizSearch('')
    setBizPage(1)
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset()
    onOpenChange(next)
  }

  const mutation = useMutation({
    mutationFn: async () => api.patch('/auth/me/password', {current_password: form.current, new_password: form.next}),
    onSuccess: () => {
      setForm(EMPTY)
      setError(null)
      setDone(true)
    },
    onError: (err) => setError(translateApiError(err)),
  })

  function field(key: keyof typeof EMPTY) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setForm((f) => ({...f, [key]: value}))
      setError(null)
      if (key === 'next') setPasswordError(null)
      setDone(false)
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const password = validatePassword(form.next)
    setPasswordError(password)
    if (password) return
    if (form.next !== form.confirm) {
      setError(t('account.mismatch'))
      return
    }
    mutation.mutate()
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent ref={setDialogNode} data-testid="mi-cuenta" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('account.title')}</DialogTitle>
          <DialogDescription>{t('account.subtitle')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
            {t('account.cedula')}
          </p>
          <p className="font-mono text-sm text-foreground" data-testid="account-cedula">
            {user?.cedula}
          </p>
        </div>

        <LicenciaSstAccountSection contenedor={dialogNode} />

        <form onSubmit={handleSubmit} noValidate className="space-y-3 border-t border-border pt-4">
          <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
            {t('account.changePassword')}
          </p>
          <div className="space-y-2">
            <Label htmlFor="account-current">{t('account.currentPassword')}</Label>
            <Input
              maxLength={tope('PASS')}
              id="account-current"
              type="password"
              autoComplete="current-password"
              value={form.current}
              onChange={field('current')}
              required
            />
            {/* Someone registered through the public link never set a password: without this
                exit «contraseña actual» is a dead end. */}
            <p className="text-xs text-muted-foreground">
              {t('account.forgotCurrent')}{' '}
              <Link
                to="/reset-request"
                data-testid="account-forgot-current"
                onClick={() => handleOpenChange(false)}
                className="font-medium text-primary hover:underline">
                {t('account.forgotCurrentLink')}
              </Link>
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="account-new">{t('account.newPassword')}</Label>
            <Input
              id="account-new"
              type="password"
              autoComplete="new-password"
              value={form.next}
              onChange={field('next')}
              maxLength={72}
              required
              aria-invalid={!!passwordError}
            />
            <FieldError error={passwordError ?? undefined} />
            <p className="text-xs text-muted-foreground">{t('common.passwordHint')}</p>
            <p className="text-xs text-muted-foreground">{t('common.passwordHintExamples')}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="account-confirm">{t('account.confirmPassword')}</Label>
            <Input
              id="account-confirm"
              type="password"
              autoComplete="new-password"
              value={form.confirm}
              onChange={field('confirm')}
              maxLength={72}
              required
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          {done && <p className="text-sm text-primary">{t('account.changed')}</p>}
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={mutation.isPending}>
              {t('account.save')}
            </Button>
          </div>
        </form>

        <div className="space-y-2 border-t border-border pt-4">
          <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
            {t('account.myBusinesses')}
          </p>
          <Input
            maxLength={tope('BUSCA')}
            data-testid="account-businesses-search"
            value={bizQuery}
            onChange={(e) => setBizQuery(e.target.value)}
            placeholder={t('account.searchBusinesses')}
          />
          {bizPageData && bizPageData.total === 0 ? (
            <p className="text-sm text-muted-foreground">{bizSearch ? t('account.noMatches') : t('account.empty')}</p>
          ) : (
            <>
              <ul data-testid="account-memberships">
                {bizPageData?.items.map((business) => (
                  <li
                    key={business.id}
                    className="flex items-center justify-between gap-3 border-t border-border py-2.5 first:border-t-0">
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-foreground">{business.name}</span>
                      <span className="font-mono text-xs text-muted-foreground">{business.nit}</span>
                    </span>
                    {/* Un rol es identidad, no estado: nunca va en la escala de estado (U2(a)). */}
                    <Tag tone="neutral" className="shrink-0">
                      {t(`roles.${business.role}`)}
                    </Tag>
                  </li>
                ))}
              </ul>
              {bizPageData && bizPageData.total > bizPageData.page_size && (
                <PaginationBar
                  page={bizPageData.page}
                  pageSize={bizPageData.page_size}
                  total={bizPageData.total}
                  onPageChange={setBizPage}
                />
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
